import { SearchIcon } from '@/components/icons'
import type { BlockConfig, BlockMeta, SubBlockConfig } from '@/blocks/types'

export const platformNames: Record<string, string> = {
  exa: 'Web Search',
  firecrawl: 'Web Content',
  parallel_ai: 'Web Research',
  serper: 'Google Search',
  serpapi: 'Search and Video',
  dataforseo: 'Keyword Data',
}
const descriptions: Record<string, string> = {
  exa: 'Search web pages, read content, and answer questions with sources.',
  firecrawl: 'Extract webpage text, discover URLs, and search the web.',
  parallel_ai: 'Search the web, extract page content, and create research reports.',
  serper: 'Search Google for pages relevant to a query.',
  serpapi: 'Search Google and read video information and transcripts.',
  dataforseo: 'Find keyword ideas and measure search volume.',
}
const operations: Record<string, [string, string][]> = {
  exa: [
    ['exa_search', 'Web Search'],
    ['exa_get_contents', 'Web Contents'],
    ['exa_answer', 'Web Answer'],
    ['exa_agent', 'Research Report'],
  ],
  firecrawl: [
    ['scrape', 'Web Extract'],
    ['map', 'Website Map'],
    ['search', 'Web Search'],
  ],
  parallel_ai: [
    ['search', 'Web Search'],
    ['extract', 'Web Extract'],
    ['deep_research', 'Research Report'],
  ],
  serper: [['', 'Google Search']],
  serpapi: [
    ['google_search', 'Google Search'],
    ['youtube_data', 'YouTube Video'],
    ['youtube_video_transcript', 'YouTube Transcript'],
  ],
  dataforseo: [
    ['keyword_ideas', 'Keyword Ideas'],
    ['search_volumes', 'Keyword Volume'],
  ],
}
export function neutralToolText(text: string): string {
  return text.replace(
    /\b(Exa(?: AI)?|Firecrawl|Parallel(?: AI)?|SerpAPI|Serper|DataForSEO|Asale)\b/gi,
    'Web Tools'
  )
}
export function platformBlock(original: BlockConfig): BlockConfig {
  const entries = operations[original.type]
  if (!entries) return original
  const query = entries.filter(([, name]) => /Search|Answer|Research/.test(name)).map(([id]) => id)
  const urls = entries.filter(([, name]) => /Contents|Extract|Map/.test(name)).map(([id]) => id)
  const keyword = original.type === 'dataforseo'
  const fields: SubBlockConfig[] = [
    {
      id: 'operation',
      title: 'Operation',
      type: 'dropdown',
      options: entries.map(([id, label]) => ({ id, label })),
      value: () => entries[0][0],
    },
  ]
  if (query.length)
    fields.push({
      id: 'query',
      title: 'Query',
      type: 'long-input',
      required: (values) => ({
        field: 'operation',
        value:
          values?.capabilityInput ||
          values?.parameters ||
          values?.search_queries ||
          values?.research_input ||
          values?.input ||
          values?.objective ||
          values?.q
            ? []
            : query,
      }),
      condition: { field: 'operation', value: query },
    })
  if (urls.length)
    fields.push({
      id: 'url',
      title: 'URL',
      type: 'short-input',
      condition: { field: 'operation', value: urls },
    })
  if (original.type === 'exa')
    fields.push({
      id: 'urls',
      title: 'URLs',
      type: 'long-input',
      condition: { field: 'operation', value: 'exa_get_contents' },
    })
  if (keyword)
    fields.push({
      id: 'keywords',
      title: 'Keywords',
      type: 'long-input',
      required: (values) => ({
        field: 'operation',
        value: values?.capabilityInput || values?.parameters ? [] : entries.map(([id]) => id),
      }),
    })
  if (original.type === 'serpapi')
    fields.push({
      id: 'videoId',
      title: 'Video ID',
      type: 'short-input',
      required: (values) => ({
        field: 'operation',
        value:
          values?.capabilityInput || values?.parameters
            ? []
            : ['youtube_data', 'youtube_video_transcript'],
      }),
      condition: { field: 'operation', value: ['youtube_data', 'youtube_video_transcript'] },
    })
  fields.push({
    id: 'capabilityInput',
    title: 'Input Parameters (JSON)',
    type: 'code',
    language: 'json',
    mode: 'advanced',
    description: 'Optional complete input object. Replaces the fields above when provided.',
  })
  const advancedFields = new Set([
    'limit',
    'numResults',
    'num',
    'type',
    'includeDomains',
    'excludeDomains',
    'category',
    'text',
    'highlights',
    'summary',
    'summaryQuery',
    'subpages',
    'subpageTarget',
    'extrasLinks',
    'extrasImageLinks',
    'outputSchema',
    'systemPrompt',
    'userLocation',
    'maxAgeHours',
    'livecrawlTimeout',
    'startPublishedDate',
    'endPublishedDate',
    'startCrawlDate',
    'endCrawlDate',
    'onlyMainContent',
    'formats',
    'waitFor',
    'mobile',
    'timeout',
    'includeTags',
    'excludeTags',
    'removeBase64Images',
    'blockAds',
    'zeroDataRetention',
    'search_queries',
    'objective',
    'extract_objective',
    'full_content',
    'research_input',
    'output_format',
    'search_mode',
    'search_include_domains',
    'search_exclude_domains',
    'include_domains',
    'exclude_domains',
    'processor',
    'max_results',
    'max_chars_per_result',
    'gl',
    'hl',
    'parameters',
  ])
  for (const field of original.subBlocks) {
    if (!advancedFields.has(field.id) || fields.some((f) => f.id === field.id)) continue
    // Search verticals are separate capabilities; the old selector changed the operation.
    if (original.type === 'serper' && field.id === 'type') continue
    fields.push({
      ...field,
      required: false,
      mode: 'advanced',
      title: neutralToolText(field.title ?? field.id),
      ...(field.description ? { description: neutralToolText(field.description) } : {}),
    })
  }
  return {
    ...original,
    name: platformNames[original.type],
    description: descriptions[original.type],
    longDescription: descriptions[original.type],
    authMode: undefined,
    icon: SearchIcon,
    bgColor: 'var(--text-tertiary)',
    iconColor: undefined,
    subBlocks: fields,
    canvasPresentation: { defaultTitle: platformNames[original.type] },
    tools: {
      ...original.tools,
      config: {
        tool: original.tools.config?.tool ?? (() => original.tools.access[0]),
        params: (p) => p,
      },
    },
    inputs: Object.fromEntries(
      ['query', 'url', 'urls', 'keywords', 'videoId', 'capabilityInput'].map((k) => [
        k,
        { type: 'string' as const, description: k },
      ])
    ),
    outputs: Object.fromEntries(
      Object.entries(original.outputs).map(([k, v]) => [
        k,
        typeof v === 'object' && 'description' in v
          ? {
              ...v,
              description: neutralToolText(v.description ?? '').replace(/Provider/gi, 'Result'),
            }
          : v,
      ])
    ) as BlockConfig['outputs'],
  }
}
export function platformMeta(meta: BlockMeta): BlockMeta {
  return { ...meta, templates: [], skills: [] }
}
